import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { getOptionLabel, getOptionValue } from "../i18n/options";

const Dropdown = ({
  label,
  options = [],
  multiSelect = false,
  searchable = false,
  withAddButton = false,
  className,
  selectedOptions = [],
  isActive,
  setIsActive = () => {},
  setSelectedOptions = () => {},
  onSelect = () => {},
  managedSelections = false,
  disabled = false,
  isInvalid = false,
  required = false,
  hideLabel = false,
  maxSelectedOptions,
  maxSelectedOptionsMessage,
}) => {
  const { t } = useTranslation();
  const [, setConfirmedSelections] = useState(
    !multiSelect && selectedOptions ? selectedOptions : []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(isActive ? true : false);
  const [displayValue, setDisplayValue] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (!multiSelect && selectedOptions && managedSelections) {
      setConfirmedSelections(selectedOptions);
    }
  }, [selectedOptions, multiSelect, managedSelections]);

  useEffect(() => {
    if (isInvalid) setHasInteracted(false);
  }, [isInvalid]);

  const handleOptionChange = (option) => {
    setHasInteracted(true);
    const value = getOptionValue(option);

    if (multiSelect) {
      const isSelected = selectedOptions.includes(value);
      if (
        !isSelected &&
        Number.isFinite(maxSelectedOptions) &&
        selectedOptions.length >= maxSelectedOptions
      ) {
        return;
      }

      const newSelection = isSelected
        ? selectedOptions.filter((item) => item !== value)
        : [...selectedOptions, value];
      setSelectedOptions(newSelection);
      if (!withAddButton) {
        setConfirmedSelections(newSelection);
        onSelect(newSelection);
        setSearchQuery("");
      }
    } else {
      setSelectedOptions([value]);
      setConfirmedSelections([value]);
      onSelect(value);
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleAddClick = () => {
    setHasInteracted(true);
    setConfirmedSelections(selectedOptions);
    onSelect(selectedOptions);
    setSearchQuery("");
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen && !isActive) setIsActive(label);
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e) => {
    setHasInteracted(true);
    setSearchQuery(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedDisplayValue = options
    .filter((option) => selectedOptions.includes(getOptionValue(option)))
    .map((option) => getOptionLabel(option))
    .join(", ");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchable && isFocused) {
      setDisplayValue(searchQuery);
    } else if (!multiSelect) {
        setDisplayValue(selectedDisplayValue || selectedOptions[0] || "");
    }
  }, [searchQuery, multiSelect, selectedOptions, searchable, isFocused, selectedDisplayValue]);

  const showInvalid = isInvalid && !hasInteracted;
  const inputClasses = showInvalid ? "input-invalid" : "input-default";

  return (
    <div className={`flex flex-col w-full ${className ?? ""}`.trim()} ref={dropdownRef}>
      {!hideLabel && (
        <label className="block text-sm font-bold text-mute-800 mb-2 flex flex-row">
          {label}
          {required && <p className="text-error-500 text-xs flex">*</p>}
        </label>
      )}
      <div className="relative">
        <div
          ref={buttonRef}
          className="w-full text-left flex items-center justify-between"
        >
          {searchable && (
            <span className="absolute left-[16px] text-mute-500">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="#b6bac3"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>
          )}
          {searchable ? (
            <input
              type="text"
              disabled={disabled}
              value={displayValue}
              onChange={handleSearchChange}
              onFocus={() => {
                handleFocus();
              }}
              onBlur={handleBlur}
              placeholder={t("dropdown.chooseOrSearch")}
              className={`${inputClasses} w-full ${
                searchable ? "input-with-icon" : ""
              }`} // Bara lägg till extra padding om searchable är true
            />
          ) : (
            <button
              type="button"
              onClick={!disabled ? toggleDropdown : undefined}
              disabled={disabled}
              className={`${inputClasses} text-left whitespace-nowrap overflow-hidden text-ellipsis appearance-none h-10 px-4 min-w-full`}
            >
              <div className="mr-10 whitespace-nowrap overflow-hidden truncate">
                {displayValue || t("dropdown.select")}
              </div>
            </button>
          )}
          <div
            className="flex items-center space-x-1 absolute right-2"
            onClick={toggleDropdown}
          >
            {multiSelect && (
              <span className="ml-auto">({selectedOptions.length})</span>
            )}
            <span className="cursor-pointer" onClick={toggleDropdown}>
              <FontAwesomeIcon
                className="cursor-pointer"
                icon={faChevronDown}
                onClick={toggleDropdown}
              />
            </span>
          </div>
        </div>
        {isOpen && (
          <div className="absolute left-0 w-full bg-white border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {withAddButton && (
              <button
                onClick={handleAddClick}
                className="w-full px-4 py-2 bg-blue-300 text-blue-700 font-semibold"
              >
                {t("common.add")}
              </button>
            )}
            {Array.isArray(filteredOptions) && filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                (() => {
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  const isSelected = selectedOptions.includes(optionValue);
                  const isMaxed =
                    multiSelect &&
                    !isSelected &&
                    Number.isFinite(maxSelectedOptions) &&
                    selectedOptions.length >= maxSelectedOptions;

                  return (
                <label
                  key={index}
                  className={`flex items-center py-2 space-x-[16px] ${
                    isMaxed ? "cursor-not-allowed opacity-50" : "hover:bg-blue-50"
                  }`}
                >
                  {multiSelect ? (
                    <>
                      <input
                        type="checkbox"
                        className="custom-checkbox-input hidden"
                        checked={isSelected}
                        disabled={isMaxed}
                        onChange={() => handleOptionChange(option)}
                      />
                      <div className="custom-checkbox">
                        <svg
                          className="custom-checkbox-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <span className="w-[13rem] truncate">{optionLabel}</span>
                    </>
                  ) : (
                    <button
                      onClick={() => handleOptionChange(option)}
                      className="block w-full text-left mx-4"
                    >
                      {optionLabel}
                    </button>
                  )}
                </label>
                  );
                })()
              ))
            ) : (
              <p className="text-black px-4 py-2">{t("dropdown.noOptions")}</p>
            )}
          </div>
        )}
      </div>
      {showInvalid && (
        <p className="text-xs text-error-400">{t("dropdown.required", { label })}</p>
      )}
      {multiSelect &&
        Number.isFinite(maxSelectedOptions) &&
        selectedOptions.length >= maxSelectedOptions &&
        maxSelectedOptionsMessage ? (
        <p className="mt-1 text-xs text-mute-500">{maxSelectedOptionsMessage}</p>
      ) : null}
    </div>
  );
};

export default Dropdown;
